import { ref, watch, onMounted, getCurrentInstance } from "vue";

type Theme = "light" | "dark";

const STORAGE_KEY = "theme";
const currentTheme = ref<Theme>("light");
let initialized = false;

export function useTheme() {
  const setTheme = (theme: Theme) => {
    currentTheme.value = theme;
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem(STORAGE_KEY, theme);
    }
    updateDocumentClass(theme);
  };

  const toggleTheme = () => {
    const newTheme = currentTheme.value === "light" ? "dark" : "light";
    setTheme(newTheme);
  };

  const updateDocumentClass = (theme: Theme) => {
    if (typeof document !== "undefined") {
      const root = document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(theme);
    }
  };

  const initTheme = () => {
    if (typeof window === "undefined") return;

    const storedTheme = localStorage.getItem(STORAGE_KEY) as Theme | null;
    let systemTheme: Theme = "light";

    if (window.matchMedia) {
      systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }

    const theme: Theme = storedTheme || systemTheme;
    currentTheme.value = theme;
    updateDocumentClass(theme);
    initialized = true;
  };

  // Only use onMounted if we're in a component context
  const instance = getCurrentInstance();
  if (instance) {
    onMounted(() => {
      if (!initialized) {
        initTheme();
      }
    });
  } else if (!initialized && typeof window !== "undefined") {
    // Initialize immediately if not in component context (e.g., tests)
    initTheme();
  }

  watch(currentTheme, (newTheme) => {
    updateDocumentClass(newTheme);
  });

  return {
    theme: currentTheme,
    setTheme,
    toggleTheme,
    initTheme,
  };
}
